<div class="panel-shell">
  <header class="panel-header primary">
    <div class="panel-title text-bold">
      {{ "QUICK_ORDER.TITLE" | translate }}
    </div>
    <button
      mat-icon-button
      type="button"
      class="panel-close-btn"
      (click)="close()"
      aria-label="Close panel"
    >
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="panel-body">
    <!-- BASE: name / date / start / end -->
    <div [formGroup]="form" class="qo-flex-column">
      <mat-form-field class="qo-width" subscriptSizing="dynamic">
        <mat-label>{{ "GENERIC.NAME" | translate }}</mat-label>
        <input
          matInput
          formControlName="name"
          type="text"
          autocomplete="nope"
          [(ngModel)]="event.name"
          required
        />
        <mat-error *ngIf="form.controls['name'].invalid">{{
          "GENERIC.INPUT_LENGTH_INVALID" | translate
        }}</mat-error>
      </mat-form-field>

      <div class="date-field-wrapper qo-width mt-12">
        <div class="timepicker-custom-label">
          {{ "GENERIC.DATE" | translate }}*
        </div>
        <mat-form-field class="qo-width">
          <input
            matInput
            [matDatepicker]="datePicker"
            [(ngModel)]="event.event_date"
            (dateChange)="onDatePickerChange($event)"
            min="1900-01-01"
            formControlName="event_date"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="datePicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #datePicker></mat-datepicker>
        </mat-form-field>
      </div>

      <div class="qo-width qo-time-field">
        <div class="timepicker-custom-label">
          {{ "GENERIC.TIME_START" | translate }}*
        </div>
        <ngx-timepicker-field
          [buttonAlign]="'left'"
          [format]="24"
          [(ngModel)]="event.event_time"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onStartTimeChange($event)"
        ></ngx-timepicker-field>
      </div>

      <div class="qo-width date-end-row">
        <button
          mat-stroked-button
          type="button"
          color="primary"
          class="copy-date-btn"
          (click)="copyStartDateToEnd()"
          [disabled]="!event.event_date"
          [matTooltip]="'QUICK_ORDER.COPY_START_DATE' | translate"
          tabindex="-1"
        >
          <mat-icon class="copy-date-icon">content_copy</mat-icon>
        </button>
        <div class="date-end-field date-field-wrapper">
          <div class="timepicker-custom-label">
            {{ "GENERIC.DATE_END" | translate }}
          </div>
          <mat-form-field class="qo-width">
            <input
              matInput
              [matDatepicker]="datePickerEnd"
              [(ngModel)]="event.event_date_end"
              [ngModelOptions]="{ standalone: true }"
              (dateChange)="onDatePickerChangeEnd($event)"
              min="1900-01-01"
            />
            <mat-datepicker-toggle
              matSuffix
              [for]="datePickerEnd"
            ></mat-datepicker-toggle>
            <mat-datepicker #datePickerEnd></mat-datepicker>
          </mat-form-field>
        </div>
      </div>

      <div class="qo-width mt-12 time-end-row">
        <button
          mat-stroked-button
          type="button"
          color="primary"
          class="add-hours-btn"
          (click)="addThreeHoursToEndTime()"
          [disabled]="!startTimeSelected"
          tabindex="-1"
        >
          +3h
        </button>
        <div class="time-end-field">
          <div class="timepicker-custom-label">
            {{ "GENERIC.TIME_END" | translate }}
          </div>
          <ngx-timepicker-field
            [buttonAlign]="'left'"
            [format]="24"
            [(ngModel)]="event.event_time_end"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onEndTimeChange($event)"
          ></ngx-timepicker-field>
        </div>
      </div>
    </div>

    <!-- CLIENTS -->
    <div class="qo-section-title text-bold mt-20">
      {{ "QUICK_ORDER.CLIENTS_TITLE" | translate }}
    </div>

    <mat-radio-group
      class="qo-radio-group mt-8"
      [(ngModel)]="clientType"
      (ngModelChange)="onClientTypeChange()"
    >
      <mat-radio-button class="qo-radio-button" value="client">
        {{ "QUICK_ORDER.BY_CLIENT" | translate }}
      </mat-radio-button>
      <mat-radio-button class="qo-radio-button" value="enterprise">
        {{ "QUICK_ORDER.BY_ENTERPRISE" | translate }}
      </mat-radio-button>
    </mat-radio-group>

    <!-- Ordering client -->
    <mat-form-field class="qo-width mt-8" subscriptSizing="dynamic">
      <mat-label>{{ "QUICK_ORDER.ORDERING_CLIENT" | translate }}</mat-label>
      <input
        matInput
        type="text"
        autocomplete="nope"
        [formControl]="orderingSearchCtrl"
        [matAutocomplete]="orderingAuto"
        (input)="onOrderingInput(orderingSearchCtrl.value)"
      />
      <mat-spinner *ngIf="orderingSearching" matSuffix [diameter]="18"></mat-spinner>
      <button
        *ngIf="orderingClient && !orderingSearching"
        mat-icon-button
        matSuffix
        type="button"
        aria-label="Clear"
        (click)="clearOrdering()"
      >
        <mat-icon>close</mat-icon>
      </button>
      <mat-autocomplete
        #orderingAuto="matAutocomplete"
        [displayWith]="displayClient"
        (optionSelected)="onOrderingSelected($event.option.value)"
      >
        <mat-option
          *ngFor="let item of orderingResults"
          [value]="item"
        >
          {{ item.name_label }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>

    <!-- Yellow button: same client for ordering & invoicing -->
    <button
      mat-raised-button
      type="button"
      class="qo-same-client-btn gt2-amber mt-12"
      [disabled]="!orderingClient"
      (click)="toggleSameClient()"
    >
      <mat-icon class="s-18">{{ sameClient ? "check_box" : "check_box_outline_blank" }}</mat-icon>
      <span>{{ "QUICK_ORDER.SAME_CLIENT_BOTH" | translate }}</span>
    </button>

    <!-- Invoicing client -->
    <mat-form-field
      class="qo-width mt-12"
      subscriptSizing="dynamic"
      *ngIf="!sameClient"
    >
      <mat-label>{{ "QUICK_ORDER.INVOICING_CLIENT" | translate }}</mat-label>
      <input
        matInput
        type="text"
        autocomplete="nope"
        [formControl]="invoicingSearchCtrl"
        [matAutocomplete]="invoicingAuto"
        (input)="onInvoicingInput(invoicingSearchCtrl.value)"
      />
      <mat-spinner *ngIf="invoicingSearching" matSuffix [diameter]="18"></mat-spinner>
      <button
        *ngIf="invoicingClient && !invoicingSearching"
        mat-icon-button
        matSuffix
        type="button"
        aria-label="Clear"
        (click)="clearInvoicing()"
      >
        <mat-icon>close</mat-icon>
      </button>
      <mat-autocomplete
        #invoicingAuto="matAutocomplete"
        [displayWith]="displayClient"
        (optionSelected)="onInvoicingSelected($event.option.value)"
      >
        <mat-option *ngFor="let item of invoicingResults" [value]="item">
          {{ item.name_label }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>

    <div class="qo-same-client-hint mt-8" *ngIf="sameClient && invoicingClient">
      {{ "QUICK_ORDER.INVOICING_CLIENT" | translate }}:
      <span class="text-bold">{{ invoicingClient.name_label }}</span>
    </div>

    <div *ngIf="loading" class="qo-flex-row-center mt-20">
      <mat-spinner [diameter]="40"></mat-spinner>
    </div>
  </div>

  <footer class="panel-footer gt2-buttons-theme flex-row-center-center">
    <button
      mat-raised-button
      color="accent"
      type="button"
      class="mr-8 ml-8 mt-4 mb-4"
      [disabled]="!canSave"
      (click)="onSave()"
    >
      {{ "GENERIC.SAVE" | translate }}
    </button>
    <button
      mat-raised-button
      type="button"
      class="mr-8 ml-8 mt-4 mb-4 primary"
      (click)="close()"
    >
      {{ "GENERIC.CANCEL" | translate }}
    </button>
  </footer>
</div>
